<script lang="ts">
  import { createEventDispatcher } from 'svelte'
  import { addToast } from '../../lib/toast'
  import { api } from '../../lib/api'

  export let sessionId: string

  const dispatch = createEventDispatcher()

  let to = ''
  let subject = ''
  let messageText = ''
  let filePath = ''
  let fileCaption = ''
  let activeTab: 'text' | 'file' = 'text'
  let sending = false
  let isHTML = false

  // Validación específica de Email
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

  function validateEmail(email: string): { valid: boolean; error?: string } {
    if (!email || email.trim() === '') {
      return { valid: false, error: 'La dirección de correo es requerida' }
    }

    const trimmed = email.trim().toLowerCase()

    if (!emailRegex.test(trimmed)) {
      return { valid: false, error: 'Formato de correo electrónico inválido. Ejemplo: usuario@dominio.com' }
    }

    return { valid: true }
  }

  function validateSubject(subject: string): { valid: boolean; error?: string } {
    if (!subject || subject.trim() === '') {
      return { valid: false, error: 'El asunto es requerido' }
    }

    if (subject.length > 255) {
      return { valid: false, error: 'El asunto no puede exceder 255 caracteres' }
    }

    return { valid: true }
  }

  function validateMessage(message: string): { valid: boolean; error?: string } {
    if (!message || message.trim() === '') {
      return { valid: false, error: 'El mensaje es requerido' }
    }

    return { valid: true }
  }

  function validateFilePath(path: string): { valid: boolean; error?: string } {
    if (!path || path.trim() === '') {
      return { valid: false, error: 'La ruta o URL del archivo es requerida' }
    }

    // Para email, solo URLs son válidas (no rutas locales)
    const isURL = /^https?:\/\/.+/.test(path.trim())

    if (!isURL) {
      return { valid: false, error: 'Debe ser una URL válida (https://...). Las rutas locales no son soportadas para correo electrónico.' }
    }

    return { valid: true }
  }

  async function sendMessage() {
    // Validar destinatario
    const emailValidation = validateEmail(to)
    if (!emailValidation.valid) {
      addToast(emailValidation.error || 'Correo inválido', 'error')
      return
    }

    if (activeTab === 'text') {
      // Validar asunto
      const subjectValidation = validateSubject(subject)
      if (!subjectValidation.valid) {
        addToast(subjectValidation.error || 'Asunto inválido', 'error')
        return
      }

      // Validar mensaje
      const msgValidation = validateMessage(messageText)
      if (!msgValidation.valid) {
        addToast(msgValidation.error || 'Mensaje inválido', 'error')
        return
      }

      sending = true
      try {
        await api.post('/api/v1/sendText', {
          sessionId: sessionId,
          to: to.trim().toLowerCase(),
          text: messageText,
          subject: subject.trim()
        })
        addToast('Correo enviado correctamente', 'success')
        messageText = ''
        subject = ''
        to = ''
        dispatch('sent')
      } catch (e: any) {
        addToast(e.message || 'Error al enviar correo', 'error')
      } finally {
        sending = false
      }
    } else {
      // Validar asunto
      const subjectValidation = validateSubject(subject)
      if (!subjectValidation.valid) {
        addToast(subjectValidation.error || 'Asunto inválido', 'error')
        return
      }

      // Validar archivo
      const fileValidation = validateFilePath(filePath)
      if (!fileValidation.valid) {
        addToast(fileValidation.error || 'URL de archivo inválida', 'error')
        return
      }

      sending = true
      try {
        // Para email, enviar como imagen con asunto
        await api.post('/api/v1/sendImage', {
          sessionId: sessionId,
          to: to.trim().toLowerCase(),
          path: filePath.trim(),
          caption: fileCaption || subject.trim()
        })
        addToast('Correo con archivo enviado correctamente', 'success')
        filePath = ''
        fileCaption = ''
        subject = ''
        to = ''
        dispatch('sent')
      } catch (e: any) {
        addToast(e.message || 'Error al enviar correo', 'error')
      } finally {
        sending = false
      }
    }
  }
</script>

<div class="space-y-5">
  <!-- Identificador del formulario -->
  <div class="flex items-center gap-3 pb-4 border-b border-white/10">
    <div class="flex items-center justify-center w-10 h-10 rounded-xl bg-yellow-500/20 border border-yellow-500/30">
      <span class="text-2xl">📧</span>
    </div>
    <div>
      <h3 class="text-lg font-black text-white">Formulario de Correo Electrónico</h3>
      <p class="text-xs text-indigo-200/60">Envía correos electrónicos con texto y archivos adjuntos</p>
    </div>
  </div>

  <!-- Tabs -->
  <div class="flex gap-2 border-b border-white/10">
    <button
      on:click={() => activeTab = 'text'}
      class="px-6 py-3 font-bold text-sm transition-all relative {activeTab === 'text' ? 'text-pink-400' : 'text-indigo-200/60 hover:text-indigo-200'}"
    >
      💬 Mensaje de Texto
      {#if activeTab === 'text'}
        <div class="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-pink-500 to-purple-500"></div>
      {/if}
    </button>
    <button
      on:click={() => activeTab = 'file'}
      class="px-6 py-3 font-bold text-sm transition-all relative {activeTab === 'file' ? 'text-pink-400' : 'text-indigo-200/60 hover:text-indigo-200'}"
    >
      🖼️ Archivo
      {#if activeTab === 'file'}
        <div class="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-pink-500 to-purple-500"></div>
      {/if}
    </button>
  </div>

  <!-- Destinatario -->
  <div>
    <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
      Destinatario (Correo Electrónico)
    </label>
    <input 
      bind:value={to}
      type="email"
      placeholder="Ej: destinatario@email.com"
      class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
    />
    <p class="text-sm text-indigo-300/80 mt-2 ml-1 font-medium">
      Ingresa la dirección de correo del destinatario
    </p>
  </div>

  <!-- Asunto (siempre requerido para email) -->
  <div>
    <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
      Asunto <span class="text-red-400">*</span>
    </label>
    <input 
      bind:value={subject}
      placeholder="Ej: Mensaje importante"
      maxlength="255"
      class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
    />
    <p class="text-xs text-indigo-300/60 mt-1 ml-1">Máximo 255 caracteres</p>
  </div>

  <!-- Contenido según tab -->
  {#if activeTab === 'text'}
    <div>
      <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Mensaje</label>
      <textarea 
        bind:value={messageText}
        rows="6"
        placeholder="Escribe el contenido del correo aquí..."
        class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20 resize-none"
      ></textarea>
    </div>
  {:else}
    <div>
      <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">URL del archivo</label>
      <div class="relative group">
        <input 
          bind:value={filePath}
          placeholder="https://ejemplo.com/archivo.pdf"
          class="w-full px-4 py-3 pl-10 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
        />
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg class="h-5 w-5 text-indigo-200/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </div>
      </div>
      <p class="text-xs text-indigo-300/60 mt-1 ml-1">Solo se permiten URLs (https://...). Las rutas locales no son soportadas para correo electrónico.</p>
    </div>

    <div>
      <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Descripción del archivo (Opcional)</label>
      <input 
        bind:value={fileCaption}
        placeholder="Descripción del archivo adjunto..."
        class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
      />
    </div>
  {/if}

  <!-- Botón Enviar -->
  <button
    on:click={sendMessage}
    disabled={sending || !to || !subject}
    class="w-full px-6 py-4 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
  >
    {sending ? 'Enviando...' : '📧 Enviar Correo'}
  </button>
</div>

